import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
  getMetadata,
  type UploadTaskSnapshot
} from 'firebase/storage'
import { storage as _storage } from './config'
import type { FirebaseStorage } from 'firebase/storage'
import type { ApiResponse } from '@/types'

// ── Types ────────────────────────────────────────────────────────────────────

export interface CustomerXmlFile {
  /** Firebase Storage full path, e.g. customers/SVC_001/xml/backup.xml */
  path: string
  /** Display filename */
  name: string
  /** File size in bytes */
  size: number
  /** ISO timestamp of last update */
  updatedAt: string
  /** Firebase Storage download URL */
  downloadUrl: string
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const getStorage = (): FirebaseStorage => {
  if (!_storage) throw new Error('Firebase Storage is not configured.')
  return _storage
}

const buildFolderPath = (customerId: string) =>
  `customers/${customerId}/xml`

const buildFilePath = (customerId: string, fileName: string) =>
  `${buildFolderPath(customerId)}/${fileName}`

// ── Service ──────────────────────────────────────────────────────────────────

export const customerXmlService = {
  /**
   * Upload an XML file for a customer.
   * @param customerId - Firestore document ID of the customer
   * @param file - The File object to upload (must be .xml)
   * @param onProgress - Optional callback receiving 0-100 progress
   */
  async upload(
    customerId: string,
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<ApiResponse<CustomerXmlFile>> {
    try {
      if (!file.name.toLowerCase().endsWith('.xml')) {
        return { success: false, error: 'Only .xml files are allowed.' }
      }

      const path = buildFilePath(customerId, file.name)
      const storageRef = ref(getStorage(), path)
      const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: 'application/xml'
      })

      return new Promise((resolve) => {
        uploadTask.on(
          'state_changed',
          (snapshot: UploadTaskSnapshot) => {
            const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            onProgress?.(pct)
          },
          (error) => {
            resolve({ success: false, error: error.message })
          },
          async () => {
            try {
              const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref)
              const meta = await getMetadata(uploadTask.snapshot.ref)
              resolve({
                success: true,
                data: {
                  path,
                  name: file.name,
                  size: meta.size,
                  updatedAt: meta.updated,
                  downloadUrl
                }
              })
            } catch (e) {
              resolve({ success: false, error: e instanceof Error ? e.message : 'Post-upload error' })
            }
          }
        )
      })
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Upload failed' }
    }
  },

  /**
   * List all XML files uploaded for a specific customer.
   */
  async list(customerId: string): Promise<ApiResponse<CustomerXmlFile[]>> {
    try {
      const folderRef = ref(getStorage(), buildFolderPath(customerId))
      const result = await listAll(folderRef)

      const files = await Promise.all(
        result.items.map(async (itemRef) => {
          const [url, meta] = await Promise.all([
            getDownloadURL(itemRef),
            getMetadata(itemRef)
          ])
          return {
            path: itemRef.fullPath,
            name: itemRef.name,
            size: meta.size,
            updatedAt: meta.updated,
            downloadUrl: url
          } satisfies CustomerXmlFile
        })
      )

      // Sort newest first
      files.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())

      return { success: true, data: files }
    } catch (error: unknown) {
      // Firebase Storage returns a "storage/object-not-found" code when folder is empty / doesn't exist
      const code = (error as { code?: string })?.code
      if (code === 'storage/object-not-found') {
        return { success: true, data: [] }
      }
      return { success: false, error: error instanceof Error ? error.message : 'Failed to list XML files' }
    }
  },

  /**
   * Delete a specific XML file by its full storage path.
   */
  async delete(path: string): Promise<ApiResponse<void>> {
    try {
      await deleteObject(ref(getStorage(), path))
      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Delete failed' }
    }
  },

  /**
   * Fetch the raw text content of an XML file via its download URL.
   * Uses CORS-safe fetch; works because Firebase Storage download URLs are public-readable.
   */
  async fetchContent(downloadUrl: string): Promise<ApiResponse<string>> {
    try {
      const res = await fetch(downloadUrl)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const text = await res.text()
      return { success: true, data: text }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch XML content' }
    }
  },

  /**
   * Trigger a browser download of an XML file.
   */
  triggerDownload(file: CustomerXmlFile) {
    const link = document.createElement('a')
    link.href = file.downloadUrl
    link.setAttribute('download', file.name)
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  },

  /** Format a byte count to a human-readable string */
  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }
}

export default customerXmlService
